import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'
import useEmblaCarousel from 'embla-carousel-react'
import {
  Carousel,
  useMediaQuery,
  Image,
  LazyVideo,
  Box,
} from '@lounge-fe/ui-kit'
import Stack from './StackPlugin'
import Footer from '@/features/footer/components/Footer'
import { EntryPoint } from '../EntryPoint'
import { WheelGesturesPlugin } from './WheelGesturesPlugin'
import { useEffect, useMemo, type KeyboardEvent } from 'react'
import type {
  VerticalCarouselData,
  VerticalCarouselSlide,
  VerticalCarouselColumn,
} from './types'
import { BlockRenderer, type LinkBlockProps } from '../BlockRenderer'
import { twMerge } from 'tailwind-merge'

function alignmentToTextAndItems(alignment?: string): string {
  switch (alignment) {
    case 'left':
      return 'text-left items-start'
    case 'right':
      return 'text-right items-end'
    case 'center':
    default:
      return 'text-center items-center'
  }
}

function positionToClasses(position?: string): string {
  switch (position) {
    case 'top-left':
      return 'items-start justify-start'
    case 'top-center':
      return 'items-start justify-center'
    case 'top-right':
      return 'items-start justify-end'
    case 'middle-left':
      return 'items-center justify-start'
    case 'middle-center':
      return 'items-center justify-center'
    case 'middle-right':
      return 'items-center justify-end'
    case 'bottom-left':
      return 'items-end justify-start'
    case 'bottom-center':
      return 'items-end justify-center'
    case 'bottom-right':
      return 'items-end justify-end'
    default:
      return ''
  }
}

function alignmentToPositionClasses(alignment?: string): string {
  switch (alignment) {
    case 'left':
      return 'items-end justify-start'
    case 'right':
      return 'items-end justify-end'
    case 'center':
    default:
      return 'items-end justify-center'
  }
}

export const VerticalCarousel: ShopifySectionComponent<
  VerticalCarouselData
> = ({ data: { slides = [] } = {} }) => {
  const isMobile = useMediaQuery(({ screens }) => `(max-width: ${screens.md})`)

  const [emblaRef, embla] = useEmblaCarousel(
    {
      axis: 'y',
      skipSnaps: false,
    },
    [Stack(), WheelGesturesPlugin()],
  )

  const normalizedSlides = useMemo<
    { columns: VerticalCarouselColumn[]; isLight: boolean }[]
  >(() => {
    if (!slides) return []
    const perSlide = slides.map((s: VerticalCarouselSlide) => {
      const isLight = s.settings?.inverse_content ?? false
      return { columns: s.columns, isLight }
    })
    if (!isMobile) return perSlide
    return perSlide.flatMap(({ columns, isLight }) =>
      (columns || []).map((c) => ({ columns: [c], isLight })),
    )
  }, [slides, isMobile])

  useEffect(() => {
    if (!embla) return

    function updateHeaderVariant() {
      const headerElement = document.getElementById(
        'shopify-section-layout_header',
      )
      if (!headerElement) return

      const selected = embla?.selectedScrollSnap()

      if (selected !== undefined) {
        const isFooter = selected >= normalizedSlides.length
        const isLight = isFooter ? false : normalizedSlides[selected].isLight

        const lightClass = 'navigation-header--variant-transparent-light'
        const darkClass = 'navigation-header--variant-transparent-dark'
        headerElement.classList.remove(isLight ? darkClass : lightClass)
        headerElement.classList.add(isLight ? lightClass : darkClass)
      }
    }

    embla.on('select', updateHeaderVariant)
    embla.on('reInit', updateHeaderVariant)
    updateHeaderVariant()

    return () => {
      embla.off('select', updateHeaderVariant)
      embla.off('reInit', updateHeaderVariant)
    }
  }, [embla, normalizedSlides])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!embla) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      embla.scrollNext()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      embla.scrollPrev()
    }
  }

  return (
    <Carousel emblaRef={emblaRef} embla={embla}>
      <Carousel.Viewport
        className="m-auto h-[100dvh]"
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <Carousel.Container className="grid grid-flow-row h-[100dvh]">
          <>
            {normalizedSlides.map(({ columns = [], isLight }, idx) => (
              <Carousel.Slide
                key={idx}
                index={idx}
                className="relative h-[100dvh] bg-surface-secondary-default"
              >
                <div
                  className="w-full h-full inset-0 grid grid-auto-cols-max grid-flow-col"
                  aria-hidden="true"
                >
                  {columns.map((col, index) => {
                    const linkBlock = col.blocks?.find(
                      (block) => block.type === 'link',
                    )
                    const linkSettings =
                      linkBlock?.settings as unknown as LinkBlockProps

                    const hasLinkUrl = linkSettings?.link_url
                    const hasSecondaryLinkUrl = col.blocks?.some(
                      (block) =>
                        'link_url_two' in block.settings &&
                        block.settings?.link_url_two,
                    )

                    const isAnchorLink =
                      linkBlock && hasLinkUrl && !hasSecondaryLinkUrl
                    const linkUrl = linkSettings?.link_url

                    const positionClasses = col.settings?.content_position
                      ? positionToClasses(col.settings?.content_position)
                      : alignmentToPositionClasses(
                          col.settings?.content_alignment,
                        )

                    const imageSource = isMobile
                      ? col.settings?.image_mobile || col.settings?.image
                      : col.settings?.image

                    const videoSource = isMobile
                      ? col.settings?.video_mobile || col.settings?.video
                      : col.settings?.video

                    return (
                      <Box
                        key={index}
                        className="relative"
                        as={isAnchorLink ? 'a' : 'div'}
                        {...(isAnchorLink ? { href: linkUrl } : {})}
                      >
                        <div
                          className={twMerge(
                            'relative z-10 flex h-full px-4 py-10 sm:py-14 sm:px-10',
                            positionClasses,
                          )}
                        >
                          <div
                            className={twMerge(
                              isLight ? 'text-white' : 'text-black',
                              'max-w-[min(90vw,60ch)]',
                            )}
                          >
                            <div
                              className={twMerge(
                                alignmentToTextAndItems(
                                  col.settings?.content_alignment,
                                ),
                                'flex flex-col gap-4',
                              )}
                            >
                              <BlockRenderer blocks={col.blocks ?? []} />
                            </div>
                          </div>
                        </div>
                        {imageSource && !videoSource && (
                          <Image
                            className="absolute inset-0 w-full h-full object-cover"
                            data={{
                              src: imageSource,
                              width: isMobile ? 1080 : 1920,
                              height: isMobile ? 1920 : 1080,
                              crop: 'center',
                            }}
                            loading={
                              idx === 0 ||
                              idx === (embla?.selectedScrollSnap() || 0) + 1
                                ? 'eager'
                                : 'lazy'
                            }
                          />
                        )}
                        {videoSource && (
                          <LazyVideo
                            video={{
                              sources: videoSource.sources,
                              alt: videoSource.alt || '',
                              preview_image: videoSource.preview_image,
                            }}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        )}
                      </Box>
                    )
                  })}
                </div>
              </Carousel.Slide>
            ))}
            <Carousel.Slide
              key="footer"
              index={normalizedSlides.length}
              className="relative h-[100dvh] bg-white pt-[var(--header-height)]"
            >
              <EntryPoint
                id="section-footer"
                component={Footer}
                usePortal={false}
              />
            </Carousel.Slide>
          </>
        </Carousel.Container>
      </Carousel.Viewport>
    </Carousel>
  )
}
